/**
 * 后台多标签页（若依 TagsView）纯逻辑：增删、关闭邻接与固定首页。
 * 标签身份只按 pathname 去重；search 单独保存，用于切换时恢复筛选条件。
 */

export type AdminTagItem = {
  /** 规范化后的 pathname，不含 query。 */
  path: string;
  title: string;
  affix?: boolean;
  /** 最近一次访问该页时的 location.search（含前导 ?），用于恢复筛选。 */
  search?: string;
};

export const ADMIN_TAGS_HOME_PATH = "/workspace";
export const ADMIN_TAGS_HOME_TITLE = "首页";

export const ADMIN_TAGS_STORAGE_KEY = "flowermaster.admin.tags-view";

/** 动作页：进入后立即跳转/清会话，不应占用 TagsView 页签。 */
const ADMIN_TAG_EXCLUDED_PATHS = new Set<string>(["/auth/logout"]);

/** 只负责定位与重定向的入口不留页签，避免用户关闭编辑器后再次进入创建流程。 */
const ADMIN_TAG_TRANSIENT_PATHS = [
  /^\/quote\/itineraries\/[^/]+\/budget$/,
  /^\/quote\/budgets\/[^/]+\/customer-quote$/,
  /^\/supply-chain\/fulfillment-orders\/workspaces\/[^/]+$/,
  /^\/supply-chain\/fulfillment-orders\/itineraries\/[^/]+\/enter$/,
];

/**
 * 同一业务页签族：菜单入口与子 Tab 共用一个页签，避免「任务池」重名并列。
 * `root` 为仅重定向的菜单 path，本身不占用页签。
 */
const ADMIN_TAG_ROUTE_FAMILIES: ReadonlyArray<{
  root: string;
  matches: (path: string) => boolean;
}> = [
  {
    root: "/crm/collaboration/pools",
    matches: (path) =>
      path === "/crm/collaboration/pools" || path.startsWith("/crm/collaboration/pools/"),
  },
];

/** 构造固定首页标签。 */
export function createAdminHomeTag(): AdminTagItem {
  return { path: ADMIN_TAGS_HOME_PATH, title: ADMIN_TAGS_HOME_TITLE, affix: true };
}

/** 规范化 pathname：去掉尾部多余斜杠（根路径除外）。 */
export function normalizeAdminTagPathname(pathname: string): string {
  if (!pathname) {
    return "/";
  }
  return pathname.length > 1 && pathname.endsWith("/") ? pathname.slice(0, -1) : pathname;
}

/** 是否应写入 TagsView（退出登录等瞬时动作页排除）。 */
export function shouldTrackAdminTag(pathname: string): boolean {
  const path = normalizeAdminTagPathname(pathname);
  if (ADMIN_TAG_EXCLUDED_PATHS.has(path) || ADMIN_TAG_TRANSIENT_PATHS.some((pattern) => pattern.test(path))) {
    return false;
  }
  return !ADMIN_TAG_ROUTE_FAMILIES.some((family) => family.root === path);
}

/** 匹配 pathname 所属的任务池等父子路由族；无匹配则返回 undefined。 */
function findAdminTagRouteFamily(pathname: string) {
  const path = normalizeAdminTagPathname(pathname);
  return ADMIN_TAG_ROUTE_FAMILIES.find((family) => family.matches(path));
}

/**
 * 兼容旧标签 path（可能已含 query）：拆成 pathname + search。
 */
export function splitAdminTagRoute(pathWithOptionalSearch: string): {
  path: string;
  search?: string;
} {
  const questionIndex = pathWithOptionalSearch.indexOf("?");
  if (questionIndex < 0) {
    return { path: normalizeAdminTagPathname(pathWithOptionalSearch) };
  }
  const pathname = normalizeAdminTagPathname(pathWithOptionalSearch.slice(0, questionIndex));
  const search = pathWithOptionalSearch.slice(questionIndex);
  return { path: pathname, search: search === "?" ? undefined : search };
}

/** @deprecated 保留给旧测试/调用；新逻辑请用 normalizeAdminTagPathname + search。 */
export function normalizeAdminTagPath(pathname: string, search = ""): string {
  const path = normalizeAdminTagPathname(pathname);
  return `${path}${search}`;
}

/** 组装可导航地址。 */
export function buildAdminTagHref(tag: Pick<AdminTagItem, "path" | "search">): string {
  return `${tag.path}${tag.search ?? ""}`;
}

/**
 * 侧栏菜单跳转：固化当前页筛选 search，并带上目标页已保存的 search。
 * 避免左侧菜单只跳 pathname、把标签里已记住的筛选条件丢掉。
 */
export function resolveAdminMenuHref(
  targetPath: string,
  options?: {
    currentPathname?: string;
    currentSearch?: string;
    tags?: AdminTagItem[];
  },
): { href: string; tags: AdminTagItem[] } {
  const target = normalizeAdminTagPathname(targetPath);
  let tags = options?.tags ? ensureAdminHomeTag(options.tags) : readAdminTagsFromStorage();
  const currentPath = options?.currentPathname
    ? normalizeAdminTagPathname(options.currentPathname)
    : undefined;
  if (currentPath) {
    const existing = tags.find((tag) => tag.path === currentPath);
    const liveSearch = options?.currentSearch || undefined;
    tags = upsertAdminTag(tags, {
      path: currentPath,
      title: existing?.title ?? currentPath,
      affix: existing?.affix,
      search: liveSearch ?? existing?.search,
    });
  }
  const targetTag = tags.find((tag) => tag.path === target);
  return {
    href: buildAdminTagHref({ path: target, search: targetTag?.search }),
    tags,
  };
}

/** 确保首页标签始终存在且置顶；并按 pathname / 路由族去重（保留后者的 title/search）。 */
export function ensureAdminHomeTag(tags: AdminTagItem[]): AdminTagItem[] {
  const deduped: AdminTagItem[] = [];
  const seenPaths = new Set<string>();
  const seenFamilies = new Set<string>();
  for (const tag of [...tags].reverse()) {
    const split = splitAdminTagRoute(tag.path);
    const path = split.path;
    if (path === ADMIN_TAGS_HOME_PATH || !shouldTrackAdminTag(path) || seenPaths.has(path)) {
      continue;
    }
    const family = findAdminTagRouteFamily(path);
    if (family) {
      if (seenFamilies.has(family.root)) {
        continue;
      }
      seenFamilies.add(family.root);
    }
    seenPaths.add(path);
    deduped.unshift({
      path,
      title: tag.title,
      affix: false,
      search: tag.search ?? split.search,
    });
  }
  return [createAdminHomeTag(), ...deduped];
}

/** 访问路由时写入或更新标签，并保持顺序。 */
export function upsertAdminTag(
  tags: AdminTagItem[],
  next: { path: string; title: string; search?: string },
): AdminTagItem[] {
  const split = splitAdminTagRoute(next.path);
  const path = split.path;
  const search = next.search ?? split.search;
  const base = ensureAdminHomeTag(tags);

  if (path === ADMIN_TAGS_HOME_PATH) {
    // 首页标题固定为「首页」，避免与联系人/商机/客户等工作台页签重名。
    return base.map((tag) =>
      tag.path === ADMIN_TAGS_HOME_PATH
        ? { ...tag, title: ADMIN_TAGS_HOME_TITLE, search: undefined }
        : tag,
    );
  }

  // 退出登录、任务池菜单重定向入口等：只清理脏页签，不新增。
  if (!shouldTrackAdminTag(path)) {
    return base;
  }

  const family = findAdminTagRouteFamily(path);
  if (family) {
    const nextTag: AdminTagItem = {
      path,
      title: next.title,
      affix: false,
      search: search || undefined,
    };
    let replaced = false;
    const merged = base.flatMap((tag) => {
      if (!family.matches(tag.path)) {
        return [tag];
      }
      if (replaced) {
        return [];
      }
      replaced = true;
      return [{ ...nextTag, title: next.title || tag.title }];
    });
    return replaced ? merged : [...base, nextTag];
  }

  const existingIndex = base.findIndex((tag) => tag.path === path);
  if (existingIndex >= 0) {
    return base.map((tag, index) =>
      index === existingIndex
        ? {
            ...tag,
            path,
            title: next.title || tag.title,
            affix: false,
            search: search || undefined,
          }
        : tag,
    );
  }
  return [...base, { path, title: next.title, affix: false, search: search || undefined }];
}

/** 关闭标签；若关掉当前页，回退到右侧邻居，否则左侧，再否则首页。 */
export function closeAdminTag(
  tags: AdminTagItem[],
  path: string,
  activePath: string,
): { tags: AdminTagItem[]; nextActivePath: string } {
  const routePath = splitAdminTagRoute(path).path;
  const activeRoutePath = splitAdminTagRoute(activePath).path;
  const base = ensureAdminHomeTag(tags);
  const target = base.find((tag) => tag.path === routePath);
  if (!target || target.affix || target.path === ADMIN_TAGS_HOME_PATH) {
    return { tags: base, nextActivePath: activePath };
  }
  const index = base.findIndex((tag) => tag.path === routePath);
  const nextTags = base.filter((tag) => tag.path !== routePath);
  if (routePath !== activeRoutePath) {
    return { tags: nextTags, nextActivePath: activePath };
  }
  const right = nextTags[index];
  const left = nextTags[index - 1];
  const fallback = right ?? left;
  return {
    tags: nextTags,
    nextActivePath: fallback ? buildAdminTagHref(fallback) : ADMIN_TAGS_HOME_PATH,
  };
}

/** 关闭除固定页与目标页外的其他标签。 */
export function closeOtherAdminTags(tags: AdminTagItem[], targetPath: string): AdminTagItem[] {
  const routePath = splitAdminTagRoute(targetPath).path;
  return ensureAdminHomeTag(tags).filter(
    (tag) => tag.affix || tag.path === ADMIN_TAGS_HOME_PATH || tag.path === routePath,
  );
}

/** 关闭目标标签左侧全部可关闭标签；首页永不关闭。 */
export function closeLeftAdminTags(
  tags: AdminTagItem[],
  targetPath: string,
  activePath: string,
): { tags: AdminTagItem[]; nextActivePath: string } {
  const routePath = splitAdminTagRoute(targetPath).path;
  const activeRoutePath = splitAdminTagRoute(activePath).path;
  const base = ensureAdminHomeTag(tags);
  const targetIndex = base.findIndex((tag) => tag.path === routePath);
  if (targetIndex < 0) {
    return { tags: base, nextActivePath: activePath };
  }
  const nextTags = base.filter((tag, index) => {
    if (index >= targetIndex) {
      return true;
    }
    return Boolean(tag.affix) || tag.path === ADMIN_TAGS_HOME_PATH;
  });
  const activeStillOpen = nextTags.some((tag) => tag.path === activeRoutePath);
  const targetTag = nextTags.find((tag) => tag.path === routePath);
  return {
    tags: nextTags,
    nextActivePath: activeStillOpen
      ? activePath
      : targetTag
        ? buildAdminTagHref(targetTag)
        : ADMIN_TAGS_HOME_PATH,
  };
}

/** 关闭全部可关闭标签，仅保留首页。 */
export function closeAllAdminTags(tags: AdminTagItem[]): AdminTagItem[] {
  return ensureAdminHomeTag(tags).filter((tag) => tag.affix || tag.path === ADMIN_TAGS_HOME_PATH);
}

/** 从 sessionStorage 恢复标签；损坏时回退为仅首页。 */
export function readAdminTagsFromStorage(): AdminTagItem[] {
  if (typeof window === "undefined") {
    return [createAdminHomeTag()];
  }
  try {
    const raw = window.sessionStorage.getItem(ADMIN_TAGS_STORAGE_KEY);
    if (!raw) {
      return [createAdminHomeTag()];
    }
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return [createAdminHomeTag()];
    }
    const tags = parsed
      .filter((item): item is AdminTagItem => {
        if (!item || typeof item !== "object") {
          return false;
        }
        const candidate = item as AdminTagItem;
        return typeof candidate.path === "string" && typeof candidate.title === "string";
      })
      .map((item) => {
        const split = splitAdminTagRoute(item.path);
        const search =
          typeof item.search === "string" && item.search.startsWith("?")
            ? item.search
            : split.search;
        return {
          path: split.path,
          title: item.title,
          affix: split.path === ADMIN_TAGS_HOME_PATH,
          search,
        };
      });
    return ensureAdminHomeTag(tags);
  } catch {
    return [createAdminHomeTag()];
  }
}

/** 将标签写入 sessionStorage。 */
export function writeAdminTagsToStorage(tags: AdminTagItem[]): void {
  if (typeof window === "undefined") {
    return;
  }
  try {
    window.sessionStorage.setItem(ADMIN_TAGS_STORAGE_KEY, JSON.stringify(ensureAdminHomeTag(tags)));
  } catch {
    // 存储配额或隐私模式失败时忽略，不影响导航。
  }
}
